import { useComposerState } from '#pplx/frontend/composer-sdk/internal/input-state/useComposerState.js';
import { EXTERNAL_VALUE_UPDATE_TAG } from '#pplx/frontend/composer-sdk/internal/updateTags.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef } from 'react';
/**
 * Seeds the editor's initial content once, on mount, when the composer is in
 * `uncontrolled-seeded` draft mode. In `controlled` draft mode the editor is
 * kept in sync by `ControlledValuePlugin`; in `uncontrolled` draft mode the
 * editor starts empty.
 *
 * The seed captures `initialValue` at first mount and does not react to later
 * changes. An uncontrolled editor owns its content once seeded, so re-seeding
 * would clobber the user's edits.
 *
 * @example
 * ```tsx
 * <LexicalComposer initialConfig={config}>
 *   <InitialValueSeedPlugin />
 * </LexicalComposer>
 * ```
 */
export const InitialValueSeedPlugin = function InitialValueSeedPlugin() {
    const state = useComposerState();
    const [editor] = useLexicalComposerContext();
    const initialValueRef = useRef(state.draftMode === 'uncontrolled-seeded' ? state.initialValue : null);
    const didSeedRef = useRef(false);
    useEffect(() => {
        const initialValue = initialValueRef.current;
        if (initialValue === null || didSeedRef.current) {
            return;
        }
        didSeedRef.current = true;
        // The initial value seeds editor ownership once; later prop changes must
        // not replace edits made inside the uncontrolled composer.
        editor.update(() => {
            editor.setEditorState(editor.parseEditorState(initialValue));
        }, { tag: EXTERNAL_VALUE_UPDATE_TAG });
    }, [editor]);
    return null;
};
//# sourceMappingURL=InitialValueSeedPlugin.js.map